<template>
<div v-if="asyncTasks?.length" :class="{'mainColor--text async-tasks-popup': true, 'is-minimized': $store.state.isAsyncTaskPopupMinimized}">

  <div class="popup-head">

    <div class="global-status-part">

      <template v-if="$store.state.isAsyncTaskPopupMinimized">
        <div class="global-status-indicator pending-or-progress" v-if="countPendingOrInProgress">
          <b-icon
            class="icon-part"
            pack="fa"
            icon="spinner"
            custom-class="fa-spin">
          </b-icon>
          <div class="count-part">
            {{ countPendingOrInProgress }} task{{ countPendingOrInProgress === 1 ? '' : 's' }} running
          </div>
        </div>
  
        <div class="global-status-indicator completed" v-if="countCompleted">
          <i class="material-icons icon-part">check_circle</i>
          <div class="count-part">
            {{ countCompleted }}
          </div>
        </div>
  
        <div class="global-status-indicator failed" v-if="countFailed">
          <i class="material-icons icon-part">error</i>
          <div class="count-part">
            {{ countFailed }}
          </div>
        </div>
  
        <div class="global-status-indicator revoked" v-if="countRevoked">
          <i class="material-icons icon-part">cancel</i>
          <div class="count-part">
            {{ countRevoked }}
          </div>
        </div>
      </template>


    </div>

    <div>
      <button
        class="minimize-btn"
        @click="$store.commit('setIsAsyncTaskPopupMinimized', !$store.state.isAsyncTaskPopupMinimized)"
      >
        <i v-if="$store.state.isAsyncTaskPopupMinimized" class="material-icons" style="font-size: 1.7rem;">keyboard_arrow_up</i>
        <i v-else class="material-icons" style="font-size: 1.7rem;">keyboard_arrow_down</i>
      </button>
    </div>

  </div>

  <div class="tasks-list" v-if="!$store.state.isAsyncTaskPopupMinimized">
    <div v-for="task in asyncTasks" :key="task.task_id" class="async-task">
  
      <template v-if="task.is_pending_or_in_progress">
        <div class="async-task-pending-or-progress">
          <div class="mb-4 d-flex align-center gap10">
            <div class="title-part" style="flex: 1;">
              {{ getProgressLabel(task.type) }}
            </div>
            <!--<button
              v-if="!task.is_client_side"
              class="close-btn"
              aria-label="close"
              @click="abortTask(task)"
            >
              Cancel run
            </button>-->
          </div>
          <div class="progress-msg-part mb-3">
            <b-icon
              pack="fa"
              icon="spinner"
              custom-class="fa-spin">
            </b-icon>
            <span class="ml-1 text">{{ task.task_status.status === 'PENDING' ? 'Starting...' : task.task_status.msg }}</span>
          </div>
          <v-progress-linear
            rounded height="5"
            color="profileAnchorColor"
            :value="task.task_status.value || 0">
          </v-progress-linear>
        </div>
      </template>
  
      <template v-else-if="task.is_completed">
        <div class="async-task-success">
          <div class="icon-part">
            <i class="material-icons">check_circle</i>
          </div>
          <div class="title-part">
            {{ getSuccessLabel(task.type) }}
          </div>
          <div class="action-part">
            <a :href="getRouteFromTask(task)" @click.stop.prevent="acknowledgeTask(task).then(() => $router.push(getRouteFromTask(task)))">View</a>
            <button
              class="close-btn"
              aria-label="close"
              @click="acknowledgeTask(task)"
            >
              <i class="material-icons" style="font-size: 1.4rem;">close</i>
            </button>
          </div>
        </div>
      </template>
  
      <template v-else-if="task?.task_status?.status === 'FAILURE'">
        <div class="async-task-failure">
          <div class="icon-part">
            <i class="material-icons">error</i>
          </div>
          <div class="message-part">
            <div class="title-part">{{ getFailureLabel(task.type) }}</div>
            <div class="reason-part" v-if="task.task_status.msg">{{task.task_status.msg}}</div>
          </div>
          <div class="action-part">
            <router-link :to="getRouteFromTask(task)">Open</router-link>
            <button
              class="close-btn"
              aria-label="close"
              @click="acknowledgeTask(task)"
            >
              <i class="material-icons" style="font-size: 1.4rem;">close</i>
            </button>
          </div>
        </div>
      </template>
  
      <template v-else-if="task?.task_status?.status === 'REVOKED'">
        <div class="async-task-revoked">
          <div class="icon-part">
            <i class="material-icons">cancel</i>
          </div>
          <div class="message-part">
            <div class="title-part">{{ getAbortLabel(task.type) }}</div>
            <div class="reason-part" v-if="task.task_status.msg">{{task.task_status.msg}}</div>
          </div>
          <div class="action-part">
            <router-link :to="getRouteFromTask(task)">Open</router-link>
            <button
              class="close-btn"
              aria-label="close"
              @click="acknowledgeTask(task)"
            >
              <i class="material-icons" style="font-size: 1.4rem;">close</i>
            </button>
          </div>
        </div>
      </template>

    </div>
  </div>

</div>
</template>

<script>
import { setIntervalAndRunImmediately } from '@/utils'

export default {
  props: ['siteId', 'campaignId'],
  data() {
    return {
      interval: null,
      isFirstLoadMade: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    asyncTasks() {
      return [
        ...this.$store.state.asyncTasks,                     // server side tasks
        ...this.$store.state.clientSideAsyncTasks            // client side tasks
    ].sort((a, b) => new Date(b.initiated_at) - new Date(a.initiated_at))  // desc
    },
    hasPendingOrInProgressBackendTasks() {
      return this.asyncTasks.filter((task) => !task.is_client_side && task.is_pending_or_in_progress).length > 0
    },
    countPendingOrInProgress() {
      return this.asyncTasks.filter(task => !!task?.is_pending_or_in_progress).length
    },
    countCompleted() {
      return this.asyncTasks.filter(task => !!task?.is_completed).length
    },
    countFailed() {
      return this.asyncTasks.filter(task => task?.task_status?.status === 'FAILURE').length
    },
    countRevoked() {
      return this.asyncTasks.filter(task => task?.task_status?.status === 'REVOKED').length
    },
  },
  watch: {
    'campaignId': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.interval) {
          clearInterval(this.interval)
        }

        this.$store.commit("resetAsyncTasks");
  
        if (newVal) {
          this.startTaskMonitor()
        }
      }
    },
    '$store.state.triggerAsyncTasksRefresh': function () {
      this.getActiveTasks();
    }
  },
  methods: {
    abortTask (task) {
      if (task.is_client_side) {
        // TODO(hichem)
        // this.$store.commit('removeClientSideAsyncTask', task.id)
        // return Promise.resolve()
      }
      return this.$store.dispatch('abortTask', {
        id: task.id,
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            return this.getActiveTasks()
          }
        })
    },
    acknowledgeTask (task) {
      if (task.is_client_side) {
        this.$store.commit('removeClientSideAsyncTask', task.id)
        return Promise.resolve()
      }
      return this.$store.dispatch('acknowledgeTask', {
        id: task.id,
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            return this.getActiveTasks()
          }
        })
    },
    getActiveTasks () {
      if (!this.isFirstLoadMade) {
        this.isFirstLoadMade = true
      }

      return this.$store.dispatch('getActiveTasks', {
        siteId: this.siteId,
        campaignId: this.campaignId
      })
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.$store.commit("setAsyncTasks", response.data.data);
            return Promise.resolve()
          }
        })
    },
    startTaskMonitor () {
      this.interval = setIntervalAndRunImmediately(() => {
        
        // NOTE(hichem): notice how the tasks list call is only made on first mount or if the list has in-progress tasks
        // This is an optimization, so clients do not keep sending server requests if not needed

        // App components that start new tasks are supposed to manually call this.$store.commit('triggerAsyncTasksRefresh'),
        // in order to trigger this.getActiveTasks() which should make hasPendingOrInProgressBackendTasks == true

        if (!this.isFirstLoadMade || this.hasPendingOrInProgressBackendTasks) {
          this.getActiveTasks()
        }

      }, 3000)
    },
    getProgressLabel(type) {
      return {
        'KeywordResearch.task': 'Getting Keyword Insight data',
        'KeywordResearch.ai_task': 'Analyzing data with AI',
        'SupportKeywordResearch.ai_task': 'Analyzing data with AI',
        'TopLevelSupportKeywordResearch.ai_task': 'Analyzing data with AI',
        'SupportKeywordResearch.task.0': 'Getting keywords',
        'SupportKeywordResearch.task.1': 'Getting keywords data',
        'SupportKeywordResearch.task.AnalyzeClustersWithAI': 'Analyzing clusters with AI',
        'TopLevelSupportKeywordResearch.task.0': 'Getting keywords',
        'TopLevelSupportKeywordResearch.task.1': 'Getting keywords data',
        'TopLevelSupportKeywordResearch.task.AnalyzeClustersWithAI': 'Analyzing clusters with AI',
        'PageOpportunity.form': 'Getting Google Console data',
        'KeywordWizardResearch.form': 'Getting Google Console data',
        'KeywordWizardResearch.monthly_search_task': 'Getting monthly search data',
        'KeywordWizardResearch.labels_task': 'Analyzing data with AI',
        'SiloResearch.one_click_silo_task': 'Getting 1-click silo data',
        'SiloResearch.custom_silo_get_keywords_task': 'Getting custom silo keywords',
        'SiloResearch.custom_silo_get_metrics_task': 'Getting custom silo metrics',
      }[type] || `In progress: ${type}`
    },
    getSuccessLabel(type) {
      return {
        'KeywordResearch.task': 'Keyword Insight data ready',
        'KeywordResearch.ai_task': 'AI analysis ready',
        'SupportKeywordResearch.ai_task': 'AI analysis ready',
        'TopLevelSupportKeywordResearch.ai_task': 'AI analysis ready',
        'SupportKeywordResearch.task.0': 'Keywords ready',
        'SupportKeywordResearch.task.1': 'Keywords data ready',
        'SupportKeywordResearch.task.AnalyzeClustersWithAI': 'Best match clusters data ready',
        'TopLevelSupportKeywordResearch.task.0': 'Keywords ready',
        'TopLevelSupportKeywordResearch.task.1': 'Keywords data ready',
        'TopLevelSupportKeywordResearch.task.AnalyzeClustersWithAI': 'Best match clusters data ready',
        'PageOpportunity.form': 'Google Console data ready',
        'KeywordWizardResearch.form': 'Google Console data ready',
        'KeywordWizardResearch.monthly_search_task': 'Monthly search data ready',
        'KeywordWizardResearch.labels_task': 'AI analysis ready',
        'SiloResearch.one_click_silo_task': '1-click silo data ready',
        'SiloResearch.custom_silo_get_keywords_task': 'Custom silo keywords ready',
        'SiloResearch.custom_silo_get_metrics_task': 'Custom silo metrics ready',
      }[type] || `${type} succeeded`
    },
    getFailureLabel(type) {
      return {
        'KeywordResearch.task': 'Keyword Insight data task failed',
        'KeywordResearch.ai_task': 'AI analysis task failed',
        'SupportKeywordResearch.ai_task': 'AI analysis task failed',
        'TopLevelSupportKeywordResearch.ai_task': 'AI analysis task failed',
        'SupportKeywordResearch.task.0': 'Keywords task failed',
        'SupportKeywordResearch.task.1': 'Keywords data task failed',
        'SupportKeywordResearch.task.AnalyzeClustersWithAI': 'Clusters analysis failed',
        'TopLevelSupportKeywordResearch.task.0': 'Keywords task failed',
        'TopLevelSupportKeywordResearch.task.1': 'Keywords data task failed',
        'TopLevelSupportKeywordResearch.task.AnalyzeClustersWithAI': 'Clusters analysis failed',
        'PageOpportunity.form': 'Google Console data task failed',
        'KeywordWizardResearch.form': 'Google Console data task failed',
        'KeywordWizardResearch.monthly_search_task': 'Monthly search data task failed',
        'KeywordWizardResearch.labels_task': 'AI analysis task failed',
        'SiloResearch.one_click_silo_task': '1-click silo task failed',
        'SiloResearch.custom_silo_get_keywords_task': 'Custom silo keywords task failed',
        'SiloResearch.custom_silo_get_metrics_task': 'Custom silo metrics task failed',
      }[type] || `${type} failed`
    },
    getAbortLabel(type) {
      return {
        'KeywordResearch.task': 'Keyword Insight data task aborted',
        'KeywordResearch.ai_task': 'AI analysis task aborted',
        'SupportKeywordResearch.ai_task': 'AI analysis task aborted',
        'TopLevelSupportKeywordResearch.ai_task': 'AI analysis task aborted',
        'SupportKeywordResearch.task.0': 'Keywords task aborted',
        'SupportKeywordResearch.task.1': 'Keywords data task aborted',
        'SupportKeywordResearch.task.AnalyzeClustersWithAI': 'Clusters analysis aborted',
        'TopLevelSupportKeywordResearch.task.0': 'Keywords task aborted',
        'TopLevelSupportKeywordResearch.task.1': 'Keywords data task aborted',
        'TopLevelSupportKeywordResearch.task.AnalyzeClustersWithAI': 'Clusters analysis aborted',
        'PageOpportunity.form': 'Google Console data task aborted',
        'KeywordWizardResearch.form': 'Google Console data task aborted',
        'KeywordWizardResearch.monthly_search_task': 'Monthly search data task aborted',
        'KeywordWizardResearch.labels_task': 'AI analysis task aborted',
        'SiloResearch.one_click_silo_task': '1-click silo task aborted',
        'SiloResearch.custom_silo_get_keywords_task': 'Custom silo keywords task aborted',
        'SiloResearch.custom_silo_get_metrics_task': 'Custom silo metrics task aborted',
      }[type] || `${type} aborted`
    },
    getRouteFromTask(task) {
      const siteId = this.$route.params.siteId
      const campaignId = this.$route.params.campaignId
      if (task.type === 'KeywordResearch.task') {
        if (campaignId) {
          return `/sites/${siteId}/campaign/${campaignId}/keyword-insight/${task.record_id}`
        }
        return `/sites/${siteId}/order-content-only/insight/${task.record_id}`
      } else if (task.type === 'KeywordResearch.ai_task') {
        if (campaignId) {
          return `/sites/${siteId}/campaign/${campaignId}/keyword-insight/${task.record_id}`
        }
        return `/sites/${siteId}/order-content-only/insight/${task.record_id}`
      } else if (task.type === 'SupportKeywordResearch.ai_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/existing-content/supporting-keyword-research`
      } else if (task.type === 'TopLevelSupportKeywordResearch.ai_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/new-content/keyword-only`
      } else if (task.type === 'SupportKeywordResearch.task.0') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/existing-content/supporting-keyword-research`
      } else if (task.type === 'SupportKeywordResearch.task.1') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/existing-content/supporting-keyword-research`
      } else if (task.type === 'SupportKeywordResearch.task.AnalyzeClustersWithAI') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/existing-content/supporting-keyword-research`
      } else if (task.type === 'TopLevelSupportKeywordResearch.task.0') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/new-content/keyword-only`
      } else if (task.type === 'TopLevelSupportKeywordResearch.task.1') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/new-content/keyword-only`
      } else if (task.type === 'TopLevelSupportKeywordResearch.task.AnalyzeClustersWithAI') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-wizard/new-content/keyword-only`
      } else if (task.type === 'PageOpportunity.form') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/existing-content/page-opportunity`
      } else if (task.type === 'KeywordWizardResearch.form') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/existing-content/site-only`
      } else if (task.type === 'KeywordWizardResearch.monthly_search_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/existing-content/site-only`
      } else if (task.type === 'KeywordWizardResearch.labels_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/existing-content/site-only`
      } else if (task.type === 'SiloResearch.one_click_silo_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/one-click-silo/${task.record_id}`
      } else if (task.type === 'SiloResearch.custom_silo_get_keywords_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/custom-silo/${task.record_id}`
      } else if (task.type === 'SiloResearch.custom_silo_get_metrics_task') {
        return `/sites/${this.$route.params.siteId}/campaign/${this.$route.params.campaignId}/keyword-strategy/custom-silo/${task.record_metadata.siloResearchId}/metrics/${task.record_id}`
      }
    }
  },
  mounted () {
    // NOTE: actual startTaskMonitor() call is is made in the 'campaignId' watcher above
    // this.startTaskMonitor()
  },
  destroyed () {
    if (this.interval) {
      clearInterval(this.interval)
    }
    this.$store.commit("resetAsyncTasks");
  }
};
</script>

<style lang="scss" scoped>
.async-tasks-popup {
  position: fixed;
  top: auto !important;
  /* --rocky-sidebar-width (set by PopAssistantButton) keeps the popup clear
     of the Rocky panel when it's docked as a right sidebar */
  right: calc(100px + var(--rocky-sidebar-width, 0px)) !important;
  bottom: 20px !important;
  left: auto !important;
  z-index: 30;
  width: 570px;
  transition: right 0.2s ease;

  background: var(--v-cardBg-base);
  border-radius: 4px;
  box-shadow: 0px 6px 10px -1px #0000001A;

  .popup-head {
    display: flex;
    flex-direction: row;
    gap: 16px;
    padding: 20px 16px;
    align-items: center;
    background-color: var(--v-gray8Color-base);

    .global-status-part {
      flex: 1;
      display: flex;
      flex-direction: row;
      gap: 14px;
      align-items: center;

      .global-status-indicator {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;

        .icon-part.material-icons {
          font-size: 20px;
        }
        .count-part {
          flex: 1;
          font-size: 1rem;
          line-height: 1.5rem;
          font-weight: 700;
        }

        &.completed {
          .icon-part.material-icons {
            color: #26B472;
          }
        }

        &.failed {
          .icon-part.material-icons {
            color: #e4220c;
          }
        }

        &.revoked {
          .icon-part.material-icons {
            color: var(--v-grayColor-base);
          }
        }
        
      }
    }
  }

  &.is-minimized {
    width: 350px;
    .popup-head { 
      background-color: transparent;
    }
  }

  .tasks-list {
    max-height: 50vh;
    overflow-y: auto;
  }
  
  .async-task {
    padding: 20px 16px;
    position: relative;

    border-bottom: 1px solid var(--v-gray10Color-base);

    &:last-child {
      border-bottom: none;
    }

    .close-btn {
      .material-icons {
        color: #888;
        &:hover {
          color: #000;
        }
      }
    }

    .async-task-pending-or-progress {
      .title-part {
        font-size: 1rem;
        line-height: 1.5rem;
        font-weight: 700;
      }
      .progress-msg-part {
        .text {
          font-size: 0.95rem !important;
        }
      }
    }
    
    .async-task-success {
      display: flex;
      flex-direction: row;
      gap: 16px;
      .icon-part {
        color: #26B472;
        .material-icons {
          font-size: 30px;
        }
      }
      .title-part {
        flex: 1;
        font-size: 1rem;
        line-height: 1.5rem;
        font-weight: 700;
        padding-top: 3px;
      }
      .action-part {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 14px;

        padding-top: 4px;
        padding-bottom: 4px;
        text-align: right;
        a {
          font-size: 1rem;
          font-weight: 700;
          color: #FF5700;
          text-decoration: underline;

        }
      }
    }

    .async-task-failure, .async-task-revoked {
      display: flex;
      flex-direction: row;
      gap: 16px;
      .icon-part {
        color: #e4220c;
        .material-icons {
          font-size: 30px;
        }
      }

      &.async-task-revoked {
        .icon-part {
          color: var(--v-grayColor-base);
        }
      }
      

      .message-part {
        flex: 1;
        padding-top: 3px;
        
        .title-part {
          font-size: 1rem;
          line-height: 1.5rem;
          font-weight: 700;
        }
        .reason-part {
          font-size: 0.9rem;
        }
      }
      .action-part {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 14px;

        padding-top: 4px;
        padding-bottom: 4px;
        text-align: right;
        a {
          font-size: 1rem;
          font-weight: 700;
          color: #FF5700;
          text-decoration: underline;

        }
      }
    }

  }
}

@media screen and (max-width: 768px) {
  .async-tasks-popup {
    left: 20px !important;
    width: initial;
  }
}
</style>
